/* eslint-disable react/no-this-in-sfc */
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import navigatorStyles from "components/charts/modern/highcharts/navigatorStyles";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import KeyValueTooltip from "components/charts/modern/tooltip/KeyValueTooltip";
import type { TDividendsPaidByYear } from "components/company/v3/brazilian/contents/events/dividends/types";
import aeonik from "components/utils/aeonik";
import type { SeriesOptionsType } from "highcharts";
import trimEnd from "lodash/trimEnd";
import numeral from "numeral";
import { renderToStaticMarkup } from "react-dom/server";
import { useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import type { IDividendsPaidDetails } from "types/graphqlTypes";

interface AmericanDividendsPaidByYearChartProps {
  dividendsPaid: TDividendsPaidByYear[];
}

export default function AmericanDividendsPaidByYearChart({ dividendsPaid }: AmericanDividendsPaidByYearChartProps) {
  const intl = useIntl();
  const theme = getColorThemeForIndex(0);

  if (!theme) {
    throw new Error("Theme not found.");
  }

  const labels = {
    amountPaid: intl.formatMessage({
      defaultMessage: "Valor ($)",
      id: "Iwb0Ka",
      description: "Valor dos dividendos pagos em dólar",
    }),
  };

  const series: SeriesOptionsType[] = [
    {
      type: "column",
      showInNavigator: true,
      animation: false,
      name: intl.formatMessage({ defaultMessage: "Rendimentos", id: "+8A8mv" }),
      color: theme.color,
      borderColor: theme.lineColor,
      data: [...dividendsPaid]
        .sort((a, b) => a.year - b.year)
        .map((node) => ({
          x: node.year,
          y: node.amountPaid,
          custom: node,
        })),
      custom: {
        primaryColor: theme.color,
        secondaryColor: theme.lineColor,
      },
    },
  ];

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: colors.slate[100],
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: colors.slate[100],
      lineWidth: 1,
      lineColor: colors.slate[100],
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        formatter() {
          return String(this.value);
        },
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    yAxis: {
      gridLineColor: colors.slate[100],
      lineColor: colors.slate[100],
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      tickWidth: 0,
      showFirstLabel: true,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
    },
    series,
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: dividendsPaid.length > 10,
      ...navigatorStyles,
      xAxis: {
        ...navigatorStyles.xAxis,
        labels: {
          formatter() {
            return String(this.value);
          },
        },
      },
      yAxis: {
        ...navigatorStyles.yAxis,
        events: {
          afterSetExtremes(this, event) {
            this.setExtremes(event.dataMin, event.dataMax);
          },
        },
        type: "linear",
      },
    },
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: false,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const point = (this.point as any).custom as IDividendsPaidDetails | null | undefined;
        if (!this.key || !point) {
          return "";
        }

        return renderToStaticMarkup(
          <KeyValueTooltip
            x={String(this.x)}
            keyValues={[
              {
                key: labels.amountPaid,
                value: trimEnd(trimEnd(numeral(point.amountPaid).format("0.000").replace(".", ","), "0"), ","),
              },
            ]}
          />,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: colors.slate[500],
            color: colors.slate[50],
            lineWidth: 2,
          },
        },
      },
      column: {
        dataGrouping: {
          enabled: true,
          approximation: "average",
        },
        stacking: "normal",
        borderWidth: 1.5,
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
  };

  return <HighchartsChart options={options} className="h-96 min-h-96 md:h-full md:max-h-96" />;
}
